import React, { useEffect, useState } from "react";
import Sheet from "@amzn/meridian/sheet";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import RadioButton from "@amzn/meridian/radio-button";
import Loader from "@amzn/meridian/loader";
import { useAppSelector } from "src/store/store";
import { extractSiteCode } from "src/constants/constants";
import { MatchingContainer } from "src/types/classificationTypes";
import {
  getMatchingContainers,
  addToContainer,
} from "src/services/itemClassificationService";
import PillButton from "src/components/UI/PillButton";

interface MatchingContainersSheetProps {
  open: boolean;
  asin: string;
  wasteProfileId: string;
  onClose: () => void;
  onSuccess: (containerName: string) => void;
}

const MatchingContainersSheet: React.FC<MatchingContainersSheetProps> = ({
  open,
  asin,
  wasteProfileId,
  onClose,
  onSuccess,
}) => {
  const { siteLocation } = useAppSelector((state) => state.user);
  const siteCode = extractSiteCode(siteLocation);

  const [containers, setContainers] = useState<MatchingContainer[]>([]);
  const [selectedContainerId, setSelectedContainerId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch containers when sheet opens
  useEffect(() => {
    if (!open) return;

    const fetchContainers = async () => {
      setLoading(true);
      setError(null);
      setSelectedContainerId("");

      try {
        const data = await getMatchingContainers(siteCode, wasteProfileId);
        setContainers(data);
      } catch (err) {
        console.error("[MatchingContainers] Failed to load:", err);
        setError("Failed to load containers.");
      } finally {
        setLoading(false);
      }
    };

    fetchContainers();
  }, [open, siteCode, wasteProfileId]);

  const handleAdd = async () => {
    if (!selectedContainerId) return;

    setSubmitting(true);
    setError(null);

    try {
      const selectedContainer = containers.find((c) => c.id === selectedContainerId);
      await addToContainer({ asin, container_id: selectedContainerId });
      onSuccess(selectedContainer?.name || selectedContainerId);
      onClose();
    } catch (err) {
      console.error("[MatchingContainers] Failed to add:", err);
      setError("Failed to add to container.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet type="overlay" side="bottom" open={open} onClose={onClose} spacingInset="500">
      <Column spacing="400">
        <Text type="h300">Matching Containers</Text>

        {loading && (
          <Row alignmentHorizontal="center" spacingInset="500">
            <Loader />
          </Row>
        )}

        {error && <Text color="error">{error}</Text>}

        {!loading && !error && containers.length === 0 && (
          <Text color="secondary">No matching containers found.</Text>
        )}

        {!loading &&
          containers.map((c) => (
            <RadioButton
              key={c.id}
              checked={selectedContainerId === c.id}
              onChange={() => setSelectedContainerId(c.id)}
              name="container-selection"
            >
              {c.name}
            </RadioButton>
          ))}

        {!loading && containers.length > 0 && (
          <Row widths={["fill", "fill"]} spacing="300">
            <PillButton text="CANCEL" color="neutral" onClick={onClose} disabled={submitting} />
            <PillButton
              text={submitting ? "Adding..." : "ADD"}
              color="success"
              onClick={handleAdd}
              disabled={!selectedContainerId || submitting}
            />
          </Row>
        )}
      </Column>
    </Sheet>
  );
};

export default MatchingContainersSheet;
